/**
 * WorkspaceTransitionOverlay — the full-screen, interaction-blocking
 * organization-change announcement, plus its post-reload continuation.
 * Owner directive 2026-08-19 established the sealed blocking flow ("not
 * allowing the user to touch anything else"); 2026-08-20 revised the pacing
 * and look: faster (1.2s floor, overlapped with the real work — see
 * workspaceTransition.ts), fully OPAQUE warm-black scrim (no page
 * bleed-through, no focus box), light wordmark, no workspace dot, and a
 * short fade-out when the destination resolves.
 *
 * Mounted ONCE at app level (WorkspaceTransitionHost). Two triggers:
 *   - a live transition begun via beginWorkspaceTransition (any switch
 *     path: foreign-thread open, WorkspacePill, login card, Organization
 *     page, onboarding launch) — phase 'switching' / 'error';
 *   - the BOOT continuation: after the hard navigation, the sessionStorage
 *     marker re-raises the same overlay until the destination workspace
 *     actually RESOLVES (onActiveWorkspaceResolved), so no pre-switch or
 *     mixed-org frame can ever reach the user's eyes ("display information
 *     before the official switch" trust break). A settled-without-account
 *     signal (guest/unmigrated boots) releases promptly; a watchdog covers
 *     genuinely unresolved gates. public/index.html's splash reads the same
 *     marker so the reload gap shows this exact look, not the default
 *     splash.
 *
 * Blocking mechanics per house doctrine: body portal at BODY_PORTAL_Z_INDEX
 * (never a bespoke number), data-prest0n-portal (portalClickGuard),
 * reference-counted body scroll lock, a focus sink + document-level capture
 * swallowing every key, and NO dismiss affordance — this overlay ends only
 * by navigation, resolution, or its own timers. Deliberately non-dismissible
 * (a first for this repo): the switch is short, and a half-switched surface
 * is the exact trust break this exists to prevent.
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { BODY_PORTAL_Z_INDEX } from './GlobalChatSystem/styles';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import { Spinner } from './common/Spinner';
import BrandWordmark from './BrandWordmark';
import {
  BOOT_CONTINUATION_MAX_MS,
  BOOT_RELEASE_BEAT_MS,
  BOOT_RELEASE_FADE_MS,
  clearBootTransition,
  getWorkspaceTransitionState,
  readBootTransition,
  subscribeWorkspaceTransition,
  type WorkspaceTransitionState,
} from '../services/workspaceTransition';
import {
  getActiveWorkspaceAccount,
  onActiveWorkspaceResolved,
  onWorkspaceSettledWithoutAccount,
} from '../services/activeWorkspace';

const overlayIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const cardIn = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const Scrim = styled.div<{ $leaving?: boolean }>`
  position: fixed;
  inset: 0;
  z-index: ${BODY_PORTAL_Z_INDEX};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  /* Fully opaque (2026-08-20): the page must never bleed through — the
     94%-translucent scrim read as "a box floating over the page". */
  background: #100f0c;
  animation: ${overlayIn} 0.24s ease-out;
  opacity: ${(p) => (p.$leaving ? 0 : 1)};
  transition: opacity ${BOOT_RELEASE_FADE_MS}ms ease;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  max-width: min(440px, calc(100vw - 48px));
  text-align: center;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: var(--brand-canvas);
  animation: ${cardIn} 0.32s cubic-bezier(0.16, 1, 0.3, 1);
  /* The card is a programmatic focus sink (tabIndex=-1) — the app-global
     gold :focus outline must not paint a box on it (owner 2026-08-20). */
  &:focus {
    outline: none;
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Eyebrow = styled.div`
  font-size: 0.78rem;
  font-weight: 500;
  letter-spacing: 0.14em;
  text-transform: lowercase;
  color: rgba(250, 250, 248, 0.55);
`;

const OrgLine = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
`;

const OrgName = styled.div`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: clamp(1.4rem, 4vw, 1.9rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: var(--brand-canvas);
  overflow-wrap: anywhere;
`;

const ThreadLine = styled.div`
  font-size: 0.92rem;
  color: rgba(250, 250, 248, 0.82);
  overflow-wrap: anywhere;
`;

const UsageLine = styled.div`
  font-size: 0.78rem;
  color: rgba(198, 167, 94, 0.9);
  border: 1px solid rgba(198, 167, 94, 0.35);
  border-radius: 999px;
  padding: 4px 12px;
`;

const ErrorLine = styled.div`
  font-size: 0.92rem;
  color: rgba(250, 250, 248, 0.9);
`;

/** Everything the overlay needs to render one announcement. */
interface OverlayView {
  kind: 'switching' | 'error' | 'arriving';
  targetAccountId: string;
  label: string | null;
  threadTitle?: string;
}

const SWITCH_EYEBROW = 'changing organization';
const ARRIVE_EYEBROW = 'entering organization';

const OverlayBody: React.FC<{ view: OverlayView; leaving?: boolean }> = ({ view, leaving }) => {
  const sinkRef = useRef<HTMLDivElement>(null);
  useBodyScrollLock(true);

  // Focus sink + total key swallow: nothing behind this overlay is
  // reachable by keyboard while it is up. Capture phase, document level.
  useEffect(() => {
    sinkRef.current?.focus();
    const swallow = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener('keydown', swallow, true);
    return () => document.removeEventListener('keydown', swallow, true);
  }, []);

  const orgDisplay = view.label || 'your other organization';
  return (
    <Scrim
      data-workspace-transition-overlay=""
      data-prest0n-portal="true"
      role="status"
      aria-live="polite"
      aria-busy={view.kind !== 'error'}
      $leaving={leaving}
    >
      <Card ref={sinkRef} tabIndex={-1}>
        <BrandWordmark nonNavigating tone="onDark" />
        {view.kind === 'error' ? (
          <>
            <Eyebrow>couldn't switch</Eyebrow>
            <ErrorLine data-workspace-transition-error="">
              the organization change didn't go through — you're right where you were.
            </ErrorLine>
          </>
        ) : (
          <>
            <Eyebrow>{view.kind === 'arriving' ? ARRIVE_EYEBROW : SWITCH_EYEBROW}</Eyebrow>
            <OrgLine>
              <OrgName data-workspace-transition-org="">{orgDisplay}</OrgName>
            </OrgLine>
            {view.threadTitle && (
              <ThreadLine data-workspace-transition-thread="">
                opening “{view.threadTitle}”
              </ThreadLine>
            )}
            <UsageLine data-workspace-transition-usage="">
              work and usage here belong to {orgDisplay}
            </UsageLine>
            <Spinner $track="rgba(250, 250, 248, 0.22)" aria-hidden="true" />
          </>
        )}
      </Card>
    </Scrim>
  );
};

/**
 * App-level host: renders the overlay for live transitions AND the boot
 * continuation. Mount exactly once (App.tsx).
 */
export const WorkspaceTransitionHost: React.FC = () => {
  const [live, setLive] = useState<WorkspaceTransitionState | null>(
    () => getWorkspaceTransitionState(),
  );
  const [boot, setBoot] = useState<{ targetAccountId: string; label: string | null } | null>(() => {
    const marker = readBootTransition();
    return marker ? { targetAccountId: marker.targetAccountId, label: marker.label } : null;
  });
  const [bootLeaving, setBootLeaving] = useState(false);

  useEffect(() => subscribeWorkspaceTransition(setLive), []);

  // Boot continuation lifecycle: hold until the destination workspace
  // RESOLVES (or settles with no account, or the watchdog fires), then a
  // short beat, a fade, release + consume the marker. Every timer lives in
  // one ledger so unmount can never leak one (the pre-2026-08-20 release
  // timer leaked).
  useEffect(() => {
    if (!boot) return;
    // A re-run of this effect (dev-mode double-invoke, or a future boot
    // value) must start from a fully visible overlay — a half-finished
    // fade from a previous run may have left the leaving flag set.
    setBootLeaving(false);
    let released = false;
    const timers = new Set<number>();
    const schedule = (fn: () => void, ms: number) => {
      const id = window.setTimeout(() => {
        timers.delete(id);
        fn();
      }, ms);
      timers.add(id);
    };
    const finalize = () => {
      clearBootTransition();
      setBoot(null);
      setBootLeaving(false);
    };
    const release = (beatMs: number) => {
      if (released) return;
      released = true;
      schedule(() => {
        setBootLeaving(true);
        schedule(finalize, BOOT_RELEASE_FADE_MS);
      }, beatMs);
    };

    // Already resolved before we mounted (fast boot): the standard beat on a
    // match; a MISMATCH releases immediately — holding while announcing the
    // wrong org would be the exact trust break this overlay prevents.
    const already = getActiveWorkspaceAccount();
    if (already) {
      release(already === boot.targetAccountId ? BOOT_RELEASE_BEAT_MS : 0);
      return () => {
        for (const id of Array.from(timers)) window.clearTimeout(id);
      };
    }

    const unsubscribeResolved = onActiveWorkspaceResolved(() => {
      // Resolved — whatever org it is, the surfaces are now keyed; the beat
      // lets the first correctly-keyed frame land under the overlay.
      release(BOOT_RELEASE_BEAT_MS);
    });
    // Settled with NO workspace (guest / unmigrated): nothing will ever be
    // keyed — release now instead of riding out the watchdog (the 12s
    // signed-out hang, 2026-08-20).
    const unsubscribeSettled = onWorkspaceSettledWithoutAccount(() => release(0));
    schedule(() => release(0), BOOT_CONTINUATION_MAX_MS);
    return () => {
      unsubscribeResolved();
      unsubscribeSettled();
      for (const id of Array.from(timers)) window.clearTimeout(id);
    };
  }, [boot]);

  if (typeof document === 'undefined') return null;

  const view: OverlayView | null = live
    ? {
        kind: live.phase === 'error' ? 'error' : 'switching',
        targetAccountId: live.request.targetAccountId,
        label: live.label,
        threadTitle: live.request.threadTitle,
      }
    : boot
      ? { kind: 'arriving', targetAccountId: boot.targetAccountId, label: boot.label }
      : null;

  if (!view) return null;
  return createPortal(
    <OverlayBody view={view} leaving={!live && bootLeaving} />,
    document.body,
  );
};

export default WorkspaceTransitionHost;
